<template>
  <section class="chat-detail" :aria-label="labels.message">
    <div class="chat-detail__header">
    <header class="chat-detail__ribbon">
      <div class="chat-detail__ribbon-actions chat-detail__ribbon-actions--left">
        <button class="chat-detail__back" type="button" @click="$emit('back')">
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
          <span>{{ labels.back }}</span>
        </button>
      </div>
      <component
        :is="isOshiCustomer ? 'div' : 'a'"
        class="chat-detail__partner"
        :class="{'chat-detail__partner--link': !isOshiCustomer}"
        :href="isOshiCustomer ? undefined : oshiDetailUrl"
      >
        <img class="chat-detail__header-avatar" :src="state.icon_path" alt="">
        <span class="chat-detail__nickname" :title="state.nickname">{{ state.nickname }}</span>
      </component>
      <div class="chat-detail__ribbon-actions chat-detail__ribbon-actions--right">
        <button
          ref="menuTrigger"
          class="chat-detail__menu"
          type="button"
          :aria-label="labels.menu"
          aria-haspopup="menu"
          :aria-expanded="menuOpen"
          @click="toggleMenu"
        >
          <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
        </button>
      </div>
    </header>
      <p v-if="state.talkTheme" class="chat-detail__theme" :title="state.talkTheme"><span>{{ labels.theme_label }}：</span><strong>{{ state.talkTheme }}</strong></p>
    </div>

    <ChatModerationPanel v-if="moderationApiUrl" ref="moderationPanel" :api-url="moderationApiUrl" :csrf-token="preferencesCsrfToken" :labels="labels" :prompt-enabled="!state.moderationPromptChecked" @state-loaded="applyModerationState" @changed="applyModerationChange" @resolved="reloadModeratedHistory" />
    <div ref="messageViewport" class="chat-detail__messages" role="log" @scroll.passive="handleScroll">
      <div v-if="state.isLoading && state.messages.length > 0" class="chat-detail__history-loading" role="status" aria-live="polite">
        <span class="ec-loading__spinnerVisual" aria-hidden="true"></span>
        <span>{{ labels.loading }}</span>
      </div>
      <div v-if="state.hasError && state.messages.length === 0" class="chat-detail__status" role="alert">
        {{ labels.error }}
      </div>
      <template v-for="(message, index) in state.messages" :key="message.id">
        <div v-if="startsDate(index)" class="chat-detail__date-separator">
          <span>{{ dateLabel(message) }}</span>
        </div>

        <article
          class="chat-detail__message"
          :class="message.is_own ? 'chat-detail__message--own' : 'chat-detail__message--partner'"
          :data-message-index="index"
        >
          <div v-if="!message.is_own" class="chat-detail__avatar-slot">
            <img
              v-if="showsPartnerIcon(index)"
              class="chat-detail__avatar"
              :src="state.icon_path"
              :alt="state.nickname"
            >
          </div>

          <div v-if="message.is_own" class="chat-detail__meta chat-detail__meta--own">
            <span v-if="isRead(message)" class="chat-detail__read">{{ labels.read }}</span>
            <time>{{ timeLabel(message.sent_time) }}</time>
          </div>

          <div
            class="chat-detail__message-content"
            :class="message.is_own ? 'chat-detail__message-content--own' : 'chat-detail__message-content--partner'"
          >
            <template v-if="message.type === 'image'">
              <span
                v-if="message.image?.is_deleted"
                class="chat-detail__bubble chat-detail__bubble--image chat-detail__image-deleted"
              >
                {{ labels.image_deleted }}
              </span>
              <button
                v-else
                class="chat-detail__bubble chat-detail__bubble--image chat-detail__image-button"
                type="button"
                :aria-label="labels.image_open"
                @click="openImageViewer(message.image, $event.currentTarget)"
              >
                <img
                  :src="message.image.url"
                  :alt="labels.image_alt"
                  :width="message.image.width"
                  :height="message.image.height"
                >
              </button>
            </template>
            <button
              v-else-if="!message.is_own"
              class="chat-detail__bubble chat-detail__reaction-target"
              type="button"
              :aria-label="labels.reaction_picker"
              @click="openReactionPicker(message, $event.currentTarget)"
            >{{ message.body }}</button>
            <p v-else class="chat-detail__bubble">{{ message.body }}</p>

            <div v-if="messageReactions(message).length > 0" class="chat-detail__reactions">
              <template v-if="!message.is_own">
                <button
                  v-for="reaction in messageReactions(message)"
                  :key="reaction"
                  class="chat-detail__reaction"
                  type="button"
                  :aria-label="`${reaction} ${labels.close}`"
                  @click="removeReaction(message, reaction)"
                >{{ reaction }}</button>
              </template>
              <template v-else>
                <span
                  v-for="reaction in messageReactions(message)"
                  :key="reaction"
                  class="chat-detail__reaction chat-detail__reaction--readonly"
                >{{ reaction }}</span>
              </template>
            </div>
          </div>

          <div v-if="!message.is_own" class="chat-detail__meta chat-detail__meta--partner">
            <time>{{ timeLabel(message.sent_time) }}</time>
          </div>
        </article>
      </template>
    </div>

    <div class="chat-detail__composer">
      <div class="chat-detail__composer-actions chat-detail__composer-actions--left">
        <input
          ref="imageInput"
          class="chat-detail__file-input"
          type="file"
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          @change="handleImageSelection"
        >
        <button
          ref="imageTrigger"
          class="chat-detail__icon-button"
          type="button"
          :aria-label="labels.image_select"
          :disabled="sendLocked"
          @click="openImagePicker"
        >
          <span v-if="isImageSending" class="ec-loading__spinnerVisual chat-detail__send-spinner" aria-hidden="true"></span>
          <i v-else class="fas fa-plus" aria-hidden="true"></i>
        </button>
      </div>
      <textarea
        ref="messageInput"
        v-model="state.draft"
        class="chat-detail__input"
        :class="{'chat-detail__input--error': hasValidationError}"
        rows="1"
        :disabled="sendLocked"
        :aria-label="labels.message"
        :placeholder="labels.message"
        @input="handleInput"
        @keydown.ctrl.enter.prevent="startSend"
        @keydown.meta.enter.prevent="startSend"
      ></textarea>
      <div class="chat-detail__composer-actions chat-detail__composer-actions--right">
        <button
          v-if="hasMessage"
          ref="sendTrigger"
          class="chat-detail__icon-button chat-detail__send"
          type="button"
          :aria-label="labels.send"
          :disabled="sendLocked"
          @click="startSend"
        >
          <span v-if="isSending && isOshiCustomer" class="ec-loading__spinnerVisual chat-detail__send-spinner" aria-hidden="true"></span>
          <i v-else class="fas fa-paper-plane" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="reactionPickerOpen"
        class="chat-reaction-picker"
        role="presentation"
        @click.self="closeReactionPicker"
        @keydown.esc="closeReactionPicker"
      >
        <div
          ref="reactionPickerPanel"
          class="chat-reaction-picker__panel"
          role="dialog"
          aria-modal="true"
          :aria-label="labels.reaction_picker"
          :style="reactionPickerPosition"
        >
          <div class="chat-reaction-picker__presets">
            <button
              v-for="reaction in reactionPresets"
              :key="reaction"
              type="button"
              @click="addReaction(activeReactionMessage, reaction)"
            >{{ reaction }}</button>
          </div>
          <div class="chat-reaction-picker__custom">
            <input
              ref="reactionInput"
              v-model="reactionDraft"
              type="text"
              :class="{'chat-reaction-picker__input--error': reactionInputError}"
              :aria-label="labels.reaction_input"
              :placeholder="labels.reaction_input"
              @input="reactionInputError = false"
              @keydown.enter.prevent="addCustomReaction"
            >
            <button type="button" :aria-label="labels.reaction_add" @click="addCustomReaction">
              <i class="fas fa-plus" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="menuOpen" class="chat-action-menu" @click.self="closeMenu" @keydown.esc="closeMenu">
        <div class="chat-action-menu__panel" role="menu" :aria-label="labels.menu">
          <button v-if="!isOshiCustomer" ref="firstMenuItem" type="button" role="menuitem" :disabled="isFavoriteUpdating" @click="changeFavorite">
            {{ state.saioshiFlg ? labels.menu_favorite_remove : labels.menu_favorite_add }}
          </button>
          <button :ref="isOshiCustomer ? 'firstMenuItem' : undefined" type="button" role="menuitem" @click="openUploadManager">{{ labels.menu_uploads }}</button>
          <button v-if="state.talkTheme" type="button" role="menuitem" @click="openThemeRemove">{{ labels.menu_theme_remove }}</button>
          <button type="button" role="menuitem" @click="openReport">{{ labels.menu_report }}</button>
          <button type="button" role="menuitem" @click="openBlockConfirm">{{ state.blockedFlg ? labels.menu_unblock : labels.menu_block }}</button>
          <button v-if="isOshiCustomer" class="chat-menu-toggle" type="button" role="menuitemcheckbox" :aria-checked="state.moderationEnabled" :disabled="isModerationUpdating" @click="changeModeration">
            <span>{{ labels.moderation_enabled }}</span><span class="chat-menu-toggle__switch" :class="{'chat-menu-toggle__switch--on': state.moderationEnabled}" aria-hidden="true"></span>
          </button>
          <button v-if="isOshiCustomer" type="button" role="menuitem" :disabled="!state.isLoaded || state.isLoading" @click="openDisplayName">{{ labels.menu_display_name }}</button>
          <a v-if="!isOshiCustomer && callSlotListUrl !== ''" class="chat-action-menu__link" :href="callSlotListUrl" role="menuitem">{{ labels.menu_call_reservation }}</a>
        </div>
      </div>

      <ChatUploadManager
        ref="uploadManager"
        :labels="labels"
        :list-api-url="uploadListApiUrl"
        :delete-api-url="uploadDeleteApiUrl"
        :delete-csrf-token="uploadDeleteCsrfToken"
        :delete-limit="uploadDeleteLimit"
        :list-loader="uploadListLoader"
        :delete-loader="uploadDeleteLoader"
        @notify="emit('notify', $event)"
        @deleted="syncDeletedUploads"
      />

      <div v-if="themeRemoveOpen" class="chat-send-modal" role="dialog" aria-modal="true" :aria-label="labels.menu_theme_remove" @click.self="closeThemeRemove" @keydown.esc="closeThemeRemove">
        <div class="chat-send-modal__panel">
          <div v-if="isThemeUpdating" class="chat-send-modal__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
          <p class="chat-send-modal__message"><template v-if="themeConflict">{{ labels.theme_state_changed }}<br><strong>{{ state.talkTheme }}</strong><br></template>{{ labels.theme_remove_confirm }}</p>
          <div class="chat-send-modal__actions">
            <button type="button" :disabled="isThemeUpdating" @click="closeThemeRemove">{{ labels.cancel }}</button>
            <button ref="themeRemoveButton" class="ec-inlineBtn--action" type="button" :disabled="isThemeUpdating" @click="removeTheme">{{ labels.theme_remove_execute }}</button>
          </div>
        </div>
      </div>

      <div
        v-if="displayNameOpen"
        class="chat-send-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="labels.menu_display_name"
        @click.self="closeDisplayName"
        @keydown.esc="closeDisplayName"
      >
        <form class="chat-send-modal__panel chat-display-name-modal__panel" @submit.prevent="changeDisplayName">
          <div v-if="isDisplayNameUpdating" class="chat-send-modal__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
          <h2 class="chat-report-modal__title">{{ labels.menu_display_name }}</h2>
          <label class="chat-display-name-modal__body">
            <span class="chat-display-name-modal__label">
              <span>{{ labels.display_name_label }}</span>
              <small>{{ labels.display_name_limit }}</small>
            </span>
            <input ref="displayNameInput" v-model="displayNameDraft" type="text" :disabled="isDisplayNameUpdating">
          </label>
          <div class="chat-send-modal__actions">
            <button type="button" :disabled="isDisplayNameUpdating" @click="closeDisplayName">{{ labels.cancel }}</button>
            <button class="ec-inlineBtn--action" type="submit" :disabled="isDisplayNameUpdating">{{ labels.display_name_change }}</button>
          </div>
        </form>
      </div>

      <div
        v-if="blockConfirmOpen"
        class="chat-send-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="state.blockedFlg ? labels.menu_unblock : labels.menu_block"
        @click.self="closeBlockConfirm"
        @keydown.esc="closeBlockConfirm"
      >
        <div class="chat-send-modal__panel">
          <div v-if="isBlockUpdating" class="chat-send-modal__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
          <p class="chat-send-modal__message">{{ state.blockedFlg ? labels.unblock_confirm : labels.block_confirm }}</p>
          <div class="chat-send-modal__actions">
            <button type="button" :disabled="isBlockUpdating" @click="closeBlockConfirm">{{ labels.close }}</button>
            <button
              ref="blockConfirmButton"
              type="button"
              class="ec-inlineBtn--action"
              :disabled="isBlockUpdating"
              @click="changeBlock"
            >
              {{ state.blockedFlg ? labels.unblock_execute : labels.block_execute }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="reportOpen"
        class="chat-send-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="labels.report_title"
        @click.self="closeReport"
        @keydown.esc="closeReport"
      >
        <form class="chat-send-modal__panel chat-report-modal__panel" @submit.prevent="openReportConfirm">
          <h2 class="chat-report-modal__title">{{ labels.report_title }}</h2>
          <div class="chat-report-modal__body">
            <label for="chat-report-reason">{{ labels.report_reason }}</label>
            <select id="chat-report-reason" ref="reportReasonInput" v-model="reportReasonType">
              <option v-for="reason in reportReasons" :key="reason.value" :value="reason.value">{{ reason.label }}</option>
            </select>
            <label for="chat-report-body">{{ labels.report_body }}</label>
            <textarea id="chat-report-body" ref="reportBodyInput" v-model="reportBody" rows="8"></textarea>
          </div>
          <div class="chat-send-modal__actions">
            <button type="button" @click="closeReport">{{ labels.close }}</button>
            <button class="ec-inlineBtn--action" type="submit">{{ labels.report_execute }}</button>
          </div>
        </form>
      </div>

      <div
        v-if="reportConfirmOpen"
        class="chat-send-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="labels.report_confirm"
        @click.self="backToReport"
        @keydown.esc="backToReport"
      >
        <div class="chat-send-modal__panel chat-report-modal__panel">
          <div v-if="isReporting" class="chat-send-modal__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
          <h2 class="chat-report-modal__title">{{ labels.report_confirm }}</h2>
          <dl class="chat-report-modal__confirmation">
            <dt>{{ labels.report_reason }}</dt>
            <dd>{{ selectedReportReasonLabel }}</dd>
            <dt>{{ labels.report_body }}</dt>
            <dd>{{ normalizedReportBody }}</dd>
          </dl>
          <div class="chat-send-modal__actions">
            <button type="button" :disabled="isReporting" @click="backToReport">{{ labels.back }}</button>
            <button ref="reportSubmitButton" class="ec-inlineBtn--action" type="button" :disabled="isReporting" @click="submitReport">{{ labels.report_execute }}</button>
          </div>
        </div>
      </div>

      <div v-if="confirmOpen" class="chat-send-modal" role="dialog" aria-modal="true" :aria-label="labels.send" @keydown.esc="closeConfirm">
        <div class="chat-send-modal__panel">
          <div v-if="isSending" class="chat-send-modal__loading">
            <span class="ec-loading__spinnerVisual" aria-hidden="true"></span>
          </div>
          <p class="chat-send-modal__message">
            {{ confirmTokenParts[0] }}<strong>{{ tokenQuantity }}</strong>{{ confirmTokenParts[1] }}<br>
            {{ labels.send_confirm_question }}
          </p>
          <div class="chat-send-modal__actions">
            <button type="button" :disabled="isSending" @click="closeConfirm">{{ labels.close }}</button>
            <button ref="confirmSendButton" type="button" :disabled="isSending" @click="sendMessage">
              {{ isSending ? labels.send_processing : labels.send }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="imageConfirmOpen"
        class="chat-send-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="labels.image_select"
        @keydown.esc="closeImageConfirm"
      >
        <div class="chat-send-modal__panel chat-image-confirm__panel">
          <div v-if="isImageSending" class="chat-send-modal__loading">
            <span class="ec-loading__spinnerVisual" aria-hidden="true"></span>
          </div>
          <img class="chat-image-confirm__preview" :src="imagePreviewUrl" :alt="labels.image_alt">
          <p class="chat-send-modal__message chat-image-confirm__message">
            {{ imageConfirmTokenParts[0] }}<strong>{{ imageTokenQuantity }}</strong>{{ imageConfirmTokenParts[1] }}
          </p>
          <p class="chat-image-confirm__notice">{{ labels.image_capacity_notice }}</p>
          <div class="chat-send-modal__actions">
            <button type="button" :disabled="isImageSending" @click="closeImageConfirm">{{ labels.close }}</button>
            <button ref="imageConfirmSendButton" type="button" :disabled="isImageSending" @click="sendImage">
              {{ isImageSending ? labels.send_processing : labels.send }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="imageViewerOpen"
        class="chat-image-viewer"
        role="dialog"
        aria-modal="true"
        :aria-label="labels.image_open"
        @click.self="closeImageViewer"
        @keydown.esc="closeImageViewer"
      >
        <div class="chat-image-viewer__dialog">
          <div class="chat-image-viewer__controls">
            <button ref="imageViewerCloseButton" class="chat-image-viewer__close" type="button" :aria-label="labels.close" @click="closeImageViewer">
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
          <div class="chat-image-viewer__panel">
            <img :src="activeImageUrl" :alt="labels.image_alt">
          </div>
        </div>
      </div>
    </Teleport>
  </section>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref} from 'vue';
import ChatUploadManager from './ChatUploadManager.vue';
import ChatModerationPanel from './ChatModerationPanel.vue';
import {
  calculateChatTokenQuantity,
  chatGraphemeLength,
  hasSufficientChatTokenBalance,
} from '../services/chatToken.js';

const JST_OFFSET_MILLISECONDS = 9 * 60 * 60 * 1000;
const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;
const TEXTAREA_LINE_HEIGHT = 20;
const TEXTAREA_VERTICAL_PADDING = 16;
const TEXTAREA_MIN_HEIGHT = TEXTAREA_LINE_HEIGHT + TEXTAREA_VERTICAL_PADDING;
const TEXTAREA_MAX_HEIGHT = TEXTAREA_LINE_HEIGHT * 5 + TEXTAREA_VERTICAL_PADDING;

const props = defineProps({
  publicCustomerId: {
    type: String,
    required: true,
  },
  oshiDetailUrl: {
    type: String,
    required: true,
  },
  callSlotListUrl: {type: String, default: ''},
  state: {
    type: Object,
    required: true,
  },
  labels: {
    type: Object,
    required: true,
  },
  messagesApiUrl: {type: String, required: true},
  messageUpdatesApiUrl: {type: String, default: ''},
  pollingDisabled: {type: Boolean, default: false},
  readApiUrl: {type: String, required: true},
  readDisabled: {type: Boolean, default: false},
  moderationApiUrl: {type: String, default: ''},
  preferencesCsrfToken: {type: String, default: ''},
  readCsrfToken: {type: String, required: true},
  sendApiUrl: {type: String, required: true},
  sendCsrfToken: {type: String, required: true},
  imageSendApiUrl: {type: String, required: true},
  imageSendCsrfToken: {type: String, required: true},
  imageMaxFileSize: {type: Number, required: true},
  imageTokenQuantity: {type: Number, required: true},
  reactionApiUrlTemplate: {type: String, required: true},
  reactionCsrfToken: {type: String, required: true},
  reactionMaxCount: {type: Number, required: true},
  reactionSaveDelay: {type: Number, required: true},
  reactionPresets: {type: Array, required: true},
  uploadListApiUrl: {type: String, required: true},
  uploadDeleteApiUrl: {type: String, required: true},
  uploadDeleteCsrfToken: {type: String, required: true},
  uploadDeleteLimit: {type: Number, required: true},
  favoriteApiUrl: {type: String, required: true},
  favoriteCsrfToken: {type: String, required: true},
  blockApiUrl: {type: String, required: true},
  blockCsrfToken: {type: String, required: true},
  reportApiUrl: {type: String, required: true},
  reportCsrfToken: {type: String, required: true},
  reportReasons: {type: Array, required: true},
  reportBodyMaxLength: {type: Number, required: true},
  displayNameApiUrl: {type: String, required: true},
  displayNameCsrfToken: {type: String, required: true},
  displayNameMaxLength: {type: Number, required: true},
  themeApiUrl: {type: String, required: true},
  themeCsrfToken: {type: String, required: true},
  isOshiCustomer: {type: Boolean, required: true},
  tokenUnitLength: {type: Number, required: true},
  tokenBalanceStore: {type: Object, required: true},
  historyLoader: {type: Function, default: null},
  updateLoader: {type: Function, default: null},
  readLoader: {type: Function, default: null},
  sendLoader: {type: Function, default: null},
  imageSendLoader: {type: Function, default: null},
  reactionLoader: {type: Function, default: null},
  uploadListLoader: {type: Function, default: null},
  uploadDeleteLoader: {type: Function, default: null},
  favoriteLoader: {type: Function, default: null},
  blockLoader: {type: Function, default: null},
  reportLoader: {type: Function, default: null},
  displayNameLoader: {type: Function, default: null},
  themeDeleteLoader: {type: Function, default: null},
});

const emit = defineEmits(['back', 'read-success', 'unavailable', 'notify', 'send-success', 'favorite-success', 'display-name-success', 'error']);
const messageInput = ref(null);
const messageViewport = ref(null);
const confirmSendButton = ref(null);
const sendTrigger = ref(null);
const imageInput = ref(null);
const imageTrigger = ref(null);
const reactionPickerPanel = ref(null);
const reactionInput = ref(null);
const imageConfirmSendButton = ref(null);
const imageViewerCloseButton = ref(null);
const menuTrigger = ref(null);
const uploadManager = ref(null);
const firstMenuItem = ref(null);
const blockConfirmButton = ref(null);
const reportReasonInput = ref(null);
const reportBodyInput = ref(null);
const reportSubmitButton = ref(null);
const displayNameInput = ref(null);
const themeRemoveButton = ref(null);
const confirmOpen = ref(false);
const imageConfirmOpen = ref(false);
const imageViewerOpen = ref(false);
const reactionPickerOpen = ref(false);
const menuOpen = ref(false);
const blockConfirmOpen = ref(false);
const reportOpen = ref(false);
const reportConfirmOpen = ref(false);
const displayNameOpen = ref(false);
const themeRemoveOpen = ref(false);
const isSending = ref(false);
const isImageSending = ref(false);
const isFavoriteUpdating = ref(false);
const isBlockUpdating = ref(false);
const isReporting = ref(false);
const isDisplayNameUpdating = ref(false);
const isThemeUpdating = ref(false);
const isModerationUpdating = ref(false);
const themeConflict = ref(false);
const hasValidationError = ref(false);
const normalizedMessage = ref('');
const tokenQuantity = ref(0);
const selectedImage = ref(null);
const imagePreviewUrl = ref('');
const activeImageUrl = ref('');
const reportReasonType = ref(null);
const reportBody = ref('');
const normalizedReportBody = ref('');
const displayNameDraft = ref('');
const reactionDraft = ref('');
const reactionInputError = ref(false);
const activeReactionMessageId = ref(null);
const reactionPickerPosition = ref({});
let imageViewerTrigger = null;
let reactionPickerTrigger = null;
const hasMessage = computed(() => props.state.draft.trim().length > 0);
const sendLocked = computed(() => confirmOpen.value || imageConfirmOpen.value || isSending.value || isImageSending.value);
const confirmTokenParts = computed(() => props.labels.send_confirm_token.split('%quantity%'));
const imageConfirmTokenParts = computed(() => props.labels.send_confirm_token.split('%quantity%'));
const activeReactionMessage = computed(() => props.state.messages.find(
  (message) => String(message.id) === String(activeReactionMessageId.value),
) ?? null);
const selectedReportReasonLabel = computed(() => props.reportReasons.find(
  (reason) => reason.value === reportReasonType.value,
)?.label ?? '');
let dateChangeTimer = null;
let updateTimer = null;
let updateRequest = null;
let updatePollingEnabled = true;

const jstDateKey = (date) => {
  const jst = new Date(date.getTime() + JST_OFFSET_MILLISECONDS);

  return [
    jst.getUTCFullYear(),
    String(jst.getUTCMonth() + 1).padStart(2, '0'),
    String(jst.getUTCDate()).padStart(2, '0'),
  ].join('-');
};

const currentJstDate = ref(jstDateKey(new Date()));

const dateParts = (date) => date.split('-').map((value) => Number(value));
const calendarDay = (date) => {
  const [year, month, day] = dateParts(date);

  return Date.UTC(year, month - 1, day);
};

const startsDate = (index) => index === 0
  || props.state.messages[index - 1].sent_date !== props.state.messages[index].sent_date;
const showsPartnerIcon = (index) => index === 0
  || props.state.messages[index - 1].is_own
  || props.state.messages[index - 1].sent_date !== props.state.messages[index].sent_date;

const replaceTokens = (template, values) => Object.entries(values).reduce(
  (result, [key, value]) => result.split(`%${key}%`).join(String(value)),
  template,
);

const dateLabel = (message) => {
  const elapsedDays = Math.round((calendarDay(currentJstDate.value) - calendarDay(message.sent_date)) / DAY_MILLISECONDS);
  if (elapsedDays === 0) {
    return props.labels.today;
  }
  if (elapsedDays === 1) {
    return props.labels.yesterday;
  }

  const [, month, day] = dateParts(message.sent_date);

  return replaceTokens(props.labels.date_with_weekday, {
    month,
    day,
    weekday: props.labels.weekdays[message.weekday],
  });
};

const timeLabel = (sentTime) => replaceTokens(props.labels.time, {time: sentTime});

const normalizedDecimalId = (id) => String(id).replace(/^0+(?=\d)/, '');
const isLessThanOrEqualDecimalId = (left, right) => {
  const normalizedLeft = normalizedDecimalId(left);
  const normalizedRight = normalizedDecimalId(right);
  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length < normalizedRight.length;
  }

  return normalizedLeft.localeCompare(normalizedRight) <= 0;
};

const isRead = (message) => message.is_own
  && props.state.partner_last_read_message_id !== null
  && isLessThanOrEqualDecimalId(message.id, props.state.partner_last_read_message_id);

const requestHistory = async (cursor = null) => {
  if (props.historyLoader !== null) {
    return props.historyLoader(cursor);
  }

  return $.ajax({
    url: props.messagesApiUrl,
    method: 'POST',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify(cursor === null ? {} : {cursor}),
  });
};

const requestUpdates = async (cursor) => {
  if (props.updateLoader !== null) return props.updateLoader(cursor);
  return $.ajax({
    url: props.messageUpdatesApiUrl,
    method: 'POST',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({cursor}),
  });
};

const requestRead = async (messageId) => {
  if (props.readLoader !== null) {
    return props.readLoader(messageId);
  }

  return $.ajax({
    url: props.readApiUrl,
    method: 'POST',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({message_id: messageId, _token: props.readCsrfToken}),
  });
};

const requestSend = async (text) => {
  if (props.sendLoader !== null) {
    return props.sendLoader(text, props.state.chatRate);
  }

  return $.ajax({
    url: props.sendApiUrl,
    method: 'POST',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({text, _token: props.sendCsrfToken, chat_rate: props.state.chatRate}),
  });
};

const requestImageSend = async (image) => {
  if (props.imageSendLoader !== null) {
    return props.imageSendLoader(image);
  }
  const formData = new FormData();
  formData.append('image', image);
  formData.append('_token', props.imageSendCsrfToken);

  return $.ajax({
    url: props.imageSendApiUrl,
    method: 'POST',
    dataType: 'json',
    data: formData,
    processData: false,
    contentType: false,
  });
};

const reactionUrl = (messageId) => props.reactionApiUrlTemplate
  .replace('%public_customer_id%', encodeURIComponent(String(props.publicCustomerId)))
  .replace('%message_id%', encodeURIComponent(String(messageId)));

const requestReaction = async (messageId, reactions) => {
  if (props.reactionLoader !== null) {
    return props.reactionLoader(messageId, reactions);
  }

  return $.ajax({
    url: reactionUrl(messageId),
    method: 'PUT',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({reactions, _token: props.reactionCsrfToken}),
  });
};

const normalizedReaction = (value) => String(value).trim().normalize('NFC');
const reactionPattern = /^(?:\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3|\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?)*)$/u;
const isReactionEmoji = (value) => {
  const reaction = normalizedReaction(value);
  const segments = [...new Intl.Segmenter(undefined, {granularity: 'grapheme'}).segment(reaction)];

  return segments.length === 1 && reactionPattern.test(reaction);
};

const messageReactions = (message) => Array.isArray(message?.reactions) ? message.reactions : [];
const reactionUpdates = () => {
  if (props.state.reactionUpdates === undefined) {
    props.state.reactionUpdates = {};
  }

  return props.state.reactionUpdates;
};
const sameReactions = (left, right) => left.length === right.length
  && left.every((reaction, index) => reaction === right[index]);
const reactionUpdate = (message) => {
  const key = String(message.id);
  if (reactionUpdates()[key] === undefined) {
    reactionUpdates()[key] = {
      confirmed: [...messageReactions(message)],
      dirty: false,
      timer: null,
      saving: null,
    };
  }

  return reactionUpdates()[key];
};

const closeReactionPicker = async (restoreFocus = true) => {
  reactionPickerOpen.value = false;
  activeReactionMessageId.value = null;
  reactionDraft.value = '';
  reactionInputError.value = false;
  if (restoreFocus) {
    await nextTick();
    reactionPickerTrigger?.focus();
  }
  reactionPickerTrigger = null;
};

const openReactionPicker = async (message, trigger) => {
  if (message.is_own || message.type !== 'text') {
    return;
  }
  activeReactionMessageId.value = String(message.id);
  reactionPickerTrigger = trigger;
  const rect = trigger.getBoundingClientRect();
  reactionPickerPosition.value = {
    top: `${Math.min(window.innerHeight - 250, rect.bottom + 8)}px`,
    left: `${Math.max(8, Math.min(window.innerWidth - 320, rect.left))}px`,
  };
  reactionPickerOpen.value = true;
  await nextTick();
  reactionPickerPanel.value?.querySelector('button')?.focus();
};

const failReactionSave = (message, update, error) => {
  message.reactions = [...update.confirmed];
  update.dirty = false;
  if (update.timer !== null) {
    window.clearTimeout(update.timer);
    update.timer = null;
  }
  emit('error', {
    message: error?.responseJSON?.message ?? props.labels.reaction_save_failed,
    fatal: false,
  });
};

const drainReactionUpdate = async (message) => {
  const update = reactionUpdate(message);
  if (update.timer !== null) {
    window.clearTimeout(update.timer);
    update.timer = null;
  }
  if (update.saving !== null) {
    return update.saving;
  }

  update.saving = (async () => {
    while (update.dirty) {
      update.dirty = false;
      const reactions = [...messageReactions(message)];
      if (sameReactions(reactions, update.confirmed)) {
        continue;
      }
      try {
        const response = await requestReaction(message.id, reactions);
        if (!validResult(response)
          || String(response.result.message_id) !== String(message.id)
          || !Array.isArray(response.result.reactions)) {
          throw new Error('Invalid reaction response.');
        }
        update.confirmed = [...response.result.reactions];
        if (!update.dirty) {
          message.reactions = [...update.confirmed];
        }
      } catch (error) {
        failReactionSave(message, update, error);
        return false;
      }
    }

    return true;
  })();

  try {
    return await update.saving;
  } finally {
    update.saving = null;
  }
};

const scheduleReactionSave = (message) => {
  const update = reactionUpdate(message);
  update.dirty = true;
  if (update.timer !== null) {
    window.clearTimeout(update.timer);
  }
  update.timer = window.setTimeout(() => drainReactionUpdate(message), props.reactionSaveDelay);
};

const addReaction = async (message, value) => {
  if (message === null) {
    return;
  }
  const reaction = normalizedReaction(value);
  if (messageReactions(message).includes(reaction)) {
    return;
  }
  if (messageReactions(message).length >= props.reactionMaxCount) {
    await closeReactionPicker(false);
    emit('error', {message: props.labels.reaction_limit, fatal: false});
    return;
  }
  reactionUpdate(message);
  message.reactions = [...messageReactions(message), reaction];
  scheduleReactionSave(message);
  await closeReactionPicker();
};

const addCustomReaction = async () => {
  const reaction = normalizedReaction(reactionDraft.value);
  if (!isReactionEmoji(reaction)) {
    reactionInputError.value = true;
    emit('notify', {type: 'error', message: props.labels.reaction_invalid});
    await nextTick();
    reactionInput.value?.focus();
    return;
  }
  await addReaction(activeReactionMessage.value, reaction);
};

const removeReaction = (message, reaction) => {
  reactionUpdate(message);
  message.reactions = messageReactions(message).filter((value) => value !== reaction);
  scheduleReactionSave(message);
};

const flushPendingReactions = async () => {
  await closeReactionPicker(false);
  const results = await Promise.all(props.state.messages.map((message) => {
    const update = reactionUpdates()[String(message.id)];
    if (update === undefined || (!update.dirty && update.saving === null)) {
      return true;
    }

    return drainReactionUpdate(message);
  }));

  return results.every(Boolean);
};

const requestFavorite = async (enabled) => {
  if (props.favoriteLoader !== null) {
    return props.favoriteLoader(enabled);
  }

  return $.ajax({
    url: props.favoriteApiUrl,
    method: 'PUT',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({enabled, _token: props.favoriteCsrfToken}),
  });
};

const requestBlock = async (enabled) => {
  if (props.blockLoader !== null) {
    return props.blockLoader(enabled);
  }

  return $.ajax({
    url: props.blockApiUrl,
    method: 'PUT',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({enabled, _token: props.blockCsrfToken}),
  });
};

const requestReport = async (reasonType, body) => {
  if (props.reportLoader !== null) {
    return props.reportLoader(reasonType, body);
  }

  return $.ajax({
    url: props.reportApiUrl,
    method: 'POST',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({reason_type: reasonType, body, _token: props.reportCsrfToken}),
  });
};

const requestDisplayName = async (displayName) => {
  if (props.displayNameLoader !== null) {
    return props.displayNameLoader(displayName);
  }

  return $.ajax({
    url: props.displayNameApiUrl,
    method: 'PUT',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({display_name: displayName, _token: props.displayNameCsrfToken}),
  });
};

const requestThemeDelete = async (expectedCurrentTheme) => {
  if (props.themeDeleteLoader !== null) {
    return props.themeDeleteLoader(expectedCurrentTheme);
  }

  return $.ajax({
    url: props.themeApiUrl,
    method: 'DELETE',
    contentType: 'application/json; charset=UTF-8',
    dataType: 'json',
    data: JSON.stringify({expected_current_theme: expectedCurrentTheme, _token: props.themeCsrfToken}),
  });
};

const closeMenu = async (restoreFocus = true) => {
  menuOpen.value = false;
  if (restoreFocus) {
    await nextTick();
    menuTrigger.value?.focus();
  }
};

const toggleMenu = async () => {
  if (menuOpen.value) {
    await closeMenu();
    return;
  }
  menuOpen.value = true;
  await nextTick();
  document.querySelector('.chat-action-menu__panel button')?.focus();
};

const openThemeRemove = async () => {
  if (!props.state.talkTheme) {
    return;
  }
  await closeMenu(false);
  themeConflict.value = false;
  themeRemoveOpen.value = true;
  await nextTick();
  themeRemoveButton.value?.focus();
};

const closeThemeRemove = async () => {
  if (isThemeUpdating.value) {
    return;
  }
  themeRemoveOpen.value = false;
  themeConflict.value = false;
  await nextTick();
  menuTrigger.value?.focus();
};

const removeTheme = async () => {
  if (isThemeUpdating.value || !props.state.talkTheme) {
    return;
  }
  isThemeUpdating.value = true;
  try {
    const response = await requestThemeDelete(props.state.talkTheme);
    if (!validResult(response) || response.result.talk_theme !== null) {
      throw new Error('Invalid chat theme response.');
    }
    props.state.talkTheme = null;
    themeRemoveOpen.value = false;
    themeConflict.value = false;
    emit('notify', {type: 'success', message: props.labels.theme_removed});
  } catch (error) {
    const conflictTheme = error?.responseJSON?.result?.talk_theme;
    if (error?.status === 409 && (conflictTheme === null || typeof conflictTheme === 'string')) {
      props.state.talkTheme = conflictTheme;
      if (conflictTheme === null) {
        themeRemoveOpen.value = false;
        emit('notify', {type: 'error', message: props.labels.theme_state_changed});
      } else {
        themeConflict.value = true;
      }
    } else {
      emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.theme_remove_failed});
    }
  } finally {
    isThemeUpdating.value = false;
    await nextTick();
    (themeRemoveOpen.value ? themeRemoveButton.value : menuTrigger.value)?.focus();
  }
};

const changeFavorite = async () => {
  if (isFavoriteUpdating.value) {
    return;
  }
  isFavoriteUpdating.value = true;
  const enabled = !props.state.saioshiFlg;
  await closeMenu(false);
  try {
    const response = await requestFavorite(enabled);
    if (!validResult(response) || typeof response.result.enabled !== 'boolean') {
      throw new Error('Invalid favorite response.');
    }
    props.state.saioshiFlg = response.result.enabled;
    emit('favorite-success', props.publicCustomerId, response.result.enabled);
    emit('notify', {
      type: 'success',
      message: response.result.enabled ? props.labels.favorite_added : props.labels.favorite_removed,
    });
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.favorite_failed});
  } finally {
    isFavoriteUpdating.value = false;
    await nextTick();
    menuTrigger.value?.focus();
  }
};

const openDisplayName = async () => {
  await closeMenu(false);
  displayNameDraft.value = props.state.displayName ?? '';
  displayNameOpen.value = true;
  await nextTick();
  displayNameInput.value?.focus();
};

const closeDisplayName = async () => {
  if (isDisplayNameUpdating.value) {
    return;
  }
  displayNameOpen.value = false;
  await nextTick();
  menuTrigger.value?.focus();
};

const normalizeDisplayName = (displayName) => displayName.replace(/^[\p{Z}\s]+|[\p{Z}\s]+$/gu, '');

const changeDisplayName = async () => {
  if (isDisplayNameUpdating.value) {
    return;
  }
  const displayName = normalizeDisplayName(displayNameDraft.value);
  if (/\p{Cc}/u.test(displayNameDraft.value) || chatGraphemeLength(displayName) > props.displayNameMaxLength) {
    emit('notify', {type: 'error', message: props.labels.display_name_invalid});
    displayNameInput.value?.focus();
    return;
  }
  isDisplayNameUpdating.value = true;
  try {
    const response = await requestDisplayName(displayName);
    if (!validResult(response)
      || !Object.prototype.hasOwnProperty.call(response.result, 'display_name')
      || (response.result.display_name !== null && typeof response.result.display_name !== 'string')
      || typeof response.result.nickname !== 'string') {
      throw new Error('Invalid display name response.');
    }
    props.state.displayName = response.result.display_name;
    props.state.nickname = response.result.nickname;
    displayNameOpen.value = false;
    emit('display-name-success', props.publicCustomerId, response.result.nickname);
    emit('notify', {type: 'success', message: props.labels.display_name_changed});
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.display_name_failed});
  } finally {
    isDisplayNameUpdating.value = false;
    await nextTick();
    (displayNameOpen.value ? displayNameInput.value : menuTrigger.value)?.focus();
  }
};

const openBlockConfirm = async () => {
  await closeMenu(false);
  blockConfirmOpen.value = true;
  await nextTick();
  blockConfirmButton.value?.focus();
};

const closeBlockConfirm = async () => {
  if (isBlockUpdating.value) {
    return;
  }
  blockConfirmOpen.value = false;
  await nextTick();
  menuTrigger.value?.focus();
};

const changeBlock = async () => {
  if (isBlockUpdating.value) {
    return;
  }
  isBlockUpdating.value = true;
  const enabled = !props.state.blockedFlg;
  try {
    const response = await requestBlock(enabled);
    if (!validResult(response) || typeof response.result.enabled !== 'boolean') {
      throw new Error('Invalid block response.');
    }
    props.state.blockedFlg = response.result.enabled;
    blockConfirmOpen.value = false;
    emit('notify', {
      type: 'success',
      message: response.result.enabled ? props.labels.block_succeeded : props.labels.unblock_succeeded,
    });
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.block_failed});
  } finally {
    isBlockUpdating.value = false;
    await nextTick();
    (blockConfirmOpen.value ? blockConfirmButton.value : menuTrigger.value)?.focus();
  }
};

const normalizeReportBody = (body) => body.replace(/\r\n?/g, '\n').normalize('NFC');

const openReport = async () => {
  await closeMenu(false);
  reportReasonType.value = props.reportReasons[0]?.value ?? null;
  reportBody.value = props.labels.report_body_initial;
  reportOpen.value = true;
  await nextTick();
  reportBodyInput.value?.focus();
  reportBodyInput.value?.setSelectionRange(reportBody.value.length, reportBody.value.length);
};

const closeReport = async () => {
  reportOpen.value = false;
  reportConfirmOpen.value = false;
  await nextTick();
  menuTrigger.value?.focus();
};

const openReportConfirm = async () => {
  const normalized = normalizeReportBody(reportBody.value);
  const length = chatGraphemeLength(normalized);
  if (length < 1 || length > props.reportBodyMaxLength || selectedReportReasonLabel.value === '') {
    emit('notify', {type: 'error', message: props.labels.report_invalid});
    reportBodyInput.value?.focus();
    return;
  }
  normalizedReportBody.value = normalized;
  reportOpen.value = false;
  reportConfirmOpen.value = true;
  await nextTick();
  reportSubmitButton.value?.focus();
};

const backToReport = async () => {
  if (isReporting.value) {
    return;
  }
  reportConfirmOpen.value = false;
  reportOpen.value = true;
  await nextTick();
  reportBodyInput.value?.focus();
};

const submitReport = async () => {
  if (isReporting.value) {
    return;
  }
  isReporting.value = true;
  try {
    const response = await requestReport(reportReasonType.value, normalizedReportBody.value);
    if (response?.status !== 'OK') {
      throw new Error('Invalid report response.');
    }
    reportConfirmOpen.value = false;
    reportOpen.value = false;
    emit('notify', {type: 'success', message: props.labels.report_received});
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.report_failed});
  } finally {
    isReporting.value = false;
    await nextTick();
    (reportConfirmOpen.value ? reportSubmitButton.value : menuTrigger.value)?.focus();
  }
};

const openUploadManager = async () => {
  await closeMenu(false);
  await uploadManager.value?.open(menuTrigger.value);
};

const syncDeletedUploads = (deletedUploadIds) => {
  const deletedIds = new Set(deletedUploadIds);
  props.state.messages.forEach((message) => {
    if (message.image !== null && deletedIds.has(message.image.id)) {
      message.image.id = null;
      message.image.url = null;
      message.image.is_deleted = true;
      message.image.width = null;
      message.image.height = null;
    }
  });
};

const openImagePicker = () => {
  if (!sendLocked.value) {
    imageInput.value?.click();
  }
};

const clearSelectedImage = () => {
  if (imagePreviewUrl.value !== '') {
    URL.revokeObjectURL(imagePreviewUrl.value);
  }
  selectedImage.value = null;
  imagePreviewUrl.value = '';
};

const handleImageSelection = async (event) => {
  const image = event.target.files?.[0] ?? null;
  event.target.value = '';
  if (image === null) {
    return;
  }
  if (image.size > props.imageMaxFileSize) {
    emit('notify', {type: 'error', message: props.labels.image_too_large});
    return;
  }
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(image.type)) {
    emit('notify', {type: 'error', message: props.labels.image_unsupported});
    return;
  }
  selectedImage.value = image;
  if (props.isOshiCustomer) {
    await sendImage();
    return;
  }

  let tokenBalanceResult;
  try {
    tokenBalanceResult = await props.tokenBalanceStore.fetch();
  } catch {
    clearSelectedImage();
    emit('error', {message: props.labels.token_fetch_failed, fatal: true});
    return;
  }
  if (!hasSufficientChatTokenBalance(
    tokenBalanceResult.balances,
    props.imageTokenQuantity,
    props.publicCustomerId,
    props.state.freeChatAvailableFlg,
    new Date(),
  )) {
    clearSelectedImage();
    emit('error', {message: insufficientTokenMessage(), fatal: false, productList: true});
    return;
  }

  imagePreviewUrl.value = URL.createObjectURL(image);
  imageConfirmOpen.value = true;
  await nextTick();
  imageConfirmSendButton.value?.focus();
};

const closeImageConfirm = async () => {
  if (isImageSending.value) {
    return;
  }
  imageConfirmOpen.value = false;
  clearSelectedImage();
  await nextTick();
  imageTrigger.value?.focus();
};

const sendImage = async () => {
  if (isImageSending.value || selectedImage.value === null) {
    return;
  }
  isImageSending.value = true;
  let unavailable = false;
  try {
    const response = await requestImageSend(selectedImage.value);
    if (!validResult(response) || response.result.message === undefined) {
      throw new Error('Invalid chat image send response.');
    }
    acceptSentMessage(response.result.message);
    applyFreeChatState(response.result);
    if (!props.readDisabled) props.state.hasUnread = false;
    imageConfirmOpen.value = false;
    emit(
      'send-success',
      props.publicCustomerId,
      response.result.message,
      response.result.consumed_balances ?? [],
      response.result.token_total_quantity ?? null,
    );
    await nextTick();
    if (messageViewport.value !== null) {
      messageViewport.value.scrollTop = messageViewport.value.scrollHeight;
      props.state.scrollTop = messageViewport.value.scrollTop;
    }
  } catch (error) {
    imageConfirmOpen.value = false;
    if (error?.status === 404) {
      unavailable = true;
      emit('unavailable', error?.responseJSON?.message ?? props.labels.error);
    } else if (error?.status === 409) {
      emit('error', {message: error?.responseJSON?.message ?? props.labels.error, fatal: false});
    } else {
      emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.image_send_failed});
    }
  } finally {
    isImageSending.value = false;
    clearSelectedImage();
    if (!unavailable) {
      await nextTick();
      imageTrigger.value?.focus();
    }
  }
};

const openImageViewer = async (image, trigger) => {
  activeImageUrl.value = image.url;
  imageViewerTrigger = trigger;
  imageViewerOpen.value = true;
  await nextTick();
  imageViewerCloseButton.value?.focus();
};

const closeImageViewer = async () => {
  imageViewerOpen.value = false;
  activeImageUrl.value = '';
  const trigger = imageViewerTrigger;
  imageViewerTrigger = null;
  await nextTick();
  trigger?.focus();
};

const normalizeMessage = (text) => text
  .replace(/\r\n?/g, '\n')
  .normalize('NFC')
  .replace(/^[\p{White_Space}]+|[\p{White_Space}]+$/gu, '');
const validateMessage = (text) => {
  const normalized = normalizeMessage(text);
  const length = chatGraphemeLength(normalized);

  return {valid: length >= 1 && length <= 1500, normalized, length};
};

const insufficientTokenMessage = () => (
  props.state.freeChatEnabledFlg
  && props.state.freeChatUsedCount >= props.state.freeChatCount
    ? props.labels.free_chat_limit_precheck
    : props.labels.insufficient_token
);

const startSend = async () => {
  if (sendLocked.value) {
    return;
  }
  const validation = validateMessage(props.state.draft);
  if (!validation.valid) {
    hasValidationError.value = true;
    emit('notify', {type: 'error', message: props.labels.invalid_message});
    return;
  }
  normalizedMessage.value = validation.normalized;
  if (props.isOshiCustomer) {
    tokenQuantity.value = 0;
    await sendMessage();
    return;
  }

  tokenQuantity.value = calculateChatTokenQuantity(
    normalizedMessage.value,
    props.state.chatRate,
    props.tokenUnitLength,
  );
  let tokenBalanceResult;
  try {
    tokenBalanceResult = await props.tokenBalanceStore.fetch();
  } catch {
    emit('error', {message: props.labels.token_fetch_failed, fatal: true});
    return;
  }
  if (!hasSufficientChatTokenBalance(
    tokenBalanceResult.balances,
    tokenQuantity.value,
    props.publicCustomerId,
    props.state.freeChatAvailableFlg,
    new Date(),
  )) {
    emit('error', {message: insufficientTokenMessage(), fatal: false, productList: true});
    return;
  }

  confirmOpen.value = true;
  await nextTick();
  confirmSendButton.value?.focus();
};

const closeConfirm = async () => {
  if (isSending.value) {
    return;
  }
  confirmOpen.value = false;
  await nextTick();
  sendTrigger.value?.focus();
};

const sendMessage = async () => {
  if (isSending.value) {
    return;
  }
  isSending.value = true;
  let unavailable = false;
  try {
    const response = await requestSend(normalizedMessage.value);
    if (!validResult(response) || response.result.message === undefined) {
      throw new Error('Invalid chat send response.');
    }
    acceptSentMessage(response.result.message);
    applyFreeChatState(response.result);
    props.state.draft = '';
    if (!props.readDisabled) props.state.hasUnread = false;
    hasValidationError.value = false;
    confirmOpen.value = false;
    emit(
      'send-success',
      props.publicCustomerId,
      response.result.message,
      response.result.consumed_balances ?? [],
      response.result.token_total_quantity ?? null,
    );
    await nextTick();
    resizeInput();
    if (messageViewport.value !== null) {
      messageViewport.value.scrollTop = messageViewport.value.scrollHeight;
      props.state.scrollTop = messageViewport.value.scrollTop;
    }
  } catch (error) {
    confirmOpen.value = false;
    if (error?.status === 404) {
      unavailable = true;
      emit('unavailable', error?.responseJSON?.message ?? props.labels.error);
    } else if (error?.status === 409) {
      hasValidationError.value = true;
      emit('error', {message: error?.responseJSON?.message ?? props.labels.error, fatal: false});
    } else {
      hasValidationError.value = true;
      emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.error});
    }
  } finally {
    isSending.value = false;
    if (!unavailable) {
      await nextTick();
      (sendTrigger.value ?? messageInput.value)?.focus();
    }
  }
};

const validResult = (response) => response?.status === 'OK'
  && response.result !== null
  && typeof response.result === 'object';

const markRead = async () => {
  if (props.readDisabled || props.state.readAttempted || !props.state.hasUnread || props.state.messages.length === 0) {
    return;
  }

  props.state.readAttempted = true;
  const messageId = props.state.messages[props.state.messages.length - 1].id;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await requestRead(messageId);
      if (response?.status === 'OK') {
        props.state.hasUnread = false;
        emit('read-success', props.publicCustomerId);
        return;
      }
    } catch (error) {
      // 既読更新は1回だけ再試行し、再失敗時は画面へ通知しない。
    }
  }
};

const applyHistory = (result) => {
  props.state.nickname = result.nickname;
  props.state.displayName = result.display_name ?? null;
  props.state.icon_path = result.icon_path;
  props.state.partner_last_read_message_id = result.partner_last_read_message_id;
  props.state.chatRate = result.chat_rate;
  applyFreeChatState(result);
  props.state.saioshiFlg = result.saioshi_flg;
  props.state.blockedFlg = result.blocked_flg;
  props.state.talkTheme = result.talk_theme ?? null;
  props.state.messages = result.messages;
  props.state.nextCursor = result.next_cursor;
  props.state.hasNext = result.has_next;
  props.state.hasUnread = result.has_unread;
  props.state.isLoaded = true;
  const latest = result.messages[result.messages.length - 1];
  props.state.updateCursor = latest === undefined
    ? String(props.state.updateCursor ?? '0')
    : String(latest.id);
};

const applyFreeChatState = (result) => {
  props.state.freeChatEnabledFlg = result.free_chat_enabled_flg === true;
  props.state.freeChatCount = Number(result.free_chat_count ?? 0);
  props.state.freeChatUsedCount = Number(result.free_chat_used_count ?? 0);
  props.state.freeChatAvailableFlg = result.free_chat_available_flg === true;
};

const applyIncomingMessages = async (messages) => {
  const known = new Set(props.state.messages.map((message) => String(message.id)));
  const additions = messages.filter((message) => !known.has(String(message.id)));
  if (additions.length === 0) return;
  props.state.messages.push(...additions);
  props.state.updateCursor = String(additions[additions.length - 1].id);
  props.state.hasUnread = true;
  props.state.readAttempted = false;
  await nextTick();
  if (messageViewport.value !== null) messageViewport.value.scrollTop = messageViewport.value.scrollHeight;
  await markRead();
};

const stopUpdatePolling = () => {
  updatePollingEnabled = false;
  if (updateTimer !== null) {
    window.clearTimeout(updateTimer);
    updateTimer = null;
  }
};

const fetchIncoming = async (fromPush = false) => {
  if (updateRequest !== null) return updateRequest;
  updateRequest = (async () => {
    let hasNext = true;
    let chat = null;
    while (hasNext) {
      const response = await requestUpdates(String(props.state.updateCursor ?? '0'));
      if (!validResult(response) || !Array.isArray(response.result.messages)) throw new Error('Invalid chat updates response.');
      await applyIncomingMessages(response.result.messages);
      props.state.updateCursor = String(response.result.next_cursor);
      chat = response.result.chat ?? chat;
      hasNext = response.result.has_next === true;
    }
    if (fromPush) stopUpdatePolling();
    return chat;
  })();
  try { return await updateRequest; } finally { updateRequest = null; }
};

const scheduleUpdatePolling = () => {
  if (!updatePollingEnabled || props.pollingDisabled || updateTimer !== null || props.messageUpdatesApiUrl === '') return;
  updateTimer = window.setTimeout(async () => {
    updateTimer = null;
    try { await fetchIncoming(false); } catch (error) { /* 次回再試行する。 */ }
    scheduleUpdatePolling();
  }, 30000);
};

const moderationPanel = ref(null);
const applyModerationState = (result) => {
  props.state.moderationEnabled = result.moderation_enabled === true;
  props.state.moderationPromptChecked = true;
};
const applyModerationChange = (enabled) => {
  props.state.moderationEnabled = enabled;
  props.state.moderationPromptChecked = true;
};
const changeModeration = async () => {
  if (isModerationUpdating.value) return;
  isModerationUpdating.value = true;
  const enabled = !props.state.moderationEnabled;
  try {
    const response = await $.ajax({
      url: props.moderationApiUrl,
      method: 'PUT',
      contentType: 'application/json',
      dataType: 'json',
      data: JSON.stringify({enabled, _token: props.preferencesCsrfToken}),
    });
    if (!validResult(response) || typeof response.result.enabled !== 'boolean') throw new Error('Invalid moderation response.');
    applyModerationChange(response.result.enabled);
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.error});
  } finally {
    isModerationUpdating.value = false;
  }
};
const acceptSentMessage = (message) => {
  if (message.pending_moderation) {
    moderationPanel.value?.refresh();
    emit('notify', {type: 'success', message: props.labels.moderation_pending});
  } else {
    props.state.messages.push(message);
  }
};
const reloadModeratedHistory = async () => {
  props.state.isLoaded = false;
  await loadInitialHistory();
};
defineExpose({flushPendingReactions, fetchIncoming, stopUpdatePolling});

const unavailableMessage = (error) => error?.responseJSON?.message ?? null;

const loadInitialHistory = async () => {
  if (props.state.isLoaded || props.state.isLoading) {
    return;
  }

  props.state.isLoading = true;
  props.state.hasError = false;
  try {
    const response = await requestHistory();
    if (!validResult(response)) {
      throw new Error('Invalid chat history response.');
    }
    applyHistory(response.result);
    await nextTick();
    restoreScrollPosition();
    await markRead();
  } catch (error) {
    const message = unavailableMessage(error);
    if (error?.status === 404 && message !== null) {
      emit('unavailable', message);
    } else {
      props.state.hasError = true;
    }
  } finally {
    props.state.isLoading = false;
  }
};

const loadOlderHistory = async () => {
  if (props.state.isLoading || !props.state.hasNext || props.state.nextCursor === null || messageViewport.value === null) {
    return;
  }

  props.state.isLoading = true;
  const previousHeight = messageViewport.value.scrollHeight;
  try {
    const response = await requestHistory(props.state.nextCursor);
    if (!validResult(response)) {
      throw new Error('Invalid chat history response.');
    }
    props.state.nickname = response.result.nickname;
    props.state.displayName = response.result.display_name ?? null;
    props.state.icon_path = response.result.icon_path;
    props.state.partner_last_read_message_id = response.result.partner_last_read_message_id;
    props.state.chatRate = response.result.chat_rate;
    applyFreeChatState(response.result);
    props.state.saioshiFlg = response.result.saioshi_flg;
    props.state.blockedFlg = response.result.blocked_flg;
    props.state.talkTheme = response.result.talk_theme ?? null;
    props.state.messages = [...response.result.messages, ...props.state.messages];
    props.state.nextCursor = response.result.next_cursor;
    props.state.hasNext = response.result.has_next;
    await nextTick();
    messageViewport.value.scrollTop += messageViewport.value.scrollHeight - previousHeight;
  } catch (error) {
    props.state.hasError = true;
  } finally {
    props.state.isLoading = false;
  }
};

const handleScroll = () => {
  if (messageViewport.value === null) {
    return;
  }
  if (!props.state.scrollReset) {
    props.state.scrollTop = messageViewport.value.scrollTop;
  }

  const firstVisible = [...messageViewport.value.querySelectorAll('[data-message-index]')]
    .find((element) => element.offsetTop + element.offsetHeight >= messageViewport.value.scrollTop);
  if (firstVisible !== undefined && Number(firstVisible.dataset.messageIndex) <= 20) {
    loadOlderHistory();
  }
};

const restoreScrollPosition = () => {
  if (messageViewport.value === null) {
    return;
  }
  messageViewport.value.scrollTop = props.state.scrollTop === null
    ? messageViewport.value.scrollHeight
    : props.state.scrollTop;
  props.state.scrollReset = false;
};

const resizeInput = () => {
  if (messageInput.value === null) {
    return;
  }

  messageInput.value.style.height = 'auto';
  const contentHeight = Math.max(messageInput.value.scrollHeight, TEXTAREA_MIN_HEIGHT);
  messageInput.value.style.height = `${Math.min(contentHeight, TEXTAREA_MAX_HEIGHT)}px`;
  messageInput.value.style.overflowY = contentHeight > TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
};
const handleInput = () => {
  hasValidationError.value = false;
  resizeInput();
};

const scheduleDateChange = () => {
  const now = new Date();
  const [year, month, day] = dateParts(jstDateKey(now));
  const nextMidnight = Date.UTC(year, month - 1, day + 1) - JST_OFFSET_MILLISECONDS;
  dateChangeTimer = window.setTimeout(() => {
    currentJstDate.value = jstDateKey(new Date());
    scheduleDateChange();
  }, Math.max(1, nextMidnight - now.getTime() + 50));
};

onMounted(async () => {
  if (props.state.isLoaded) {
    await nextTick();
    restoreScrollPosition();
    await markRead();
  } else {
    await loadInitialHistory();
  }
  resizeInput();
  scheduleDateChange();
  scheduleUpdatePolling();
});

onBeforeUnmount(() => {
  clearSelectedImage();
  if (messageViewport.value !== null && !props.state.scrollReset) {
    props.state.scrollTop = messageViewport.value.scrollTop;
  }
  if (dateChangeTimer !== null) {
    window.clearTimeout(dateChangeTimer);
  }
  stopUpdatePolling();
  Object.values(reactionUpdates()).forEach((update) => {
    if (update.timer !== null) {
      window.clearTimeout(update.timer);
    }
  });
});
</script>

<style scoped>
.chat-detail {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  box-sizing: border-box;
  height: calc(100vh - 42px);
  height: calc(100dvh - 42px);
  min-width: 0;
  overflow: hidden;
  color: #26262b;
  background: #91a9bc;
}

.chat-detail__header {
  z-index: 2;
  min-width: 0;
}

:global(body.chat-detail-page) {
  overflow: hidden;
}

:global(.chat-detail-page .ec-layoutRole__contents) {
  min-height: 0;
}

:global(body.chat-detail-page .ec-layoutRole .ec-layoutRole__main.ec-layoutRole__main--subpage) {
  min-height: 0;
  padding: 0;
}

:global(body.chat-detail-page .ec-layoutRole__footer) {
  display: none;
}

.chat-detail__status {
  padding: 24px;
  color: #fff;
  text-align: center;
}

.chat-detail__send-spinner {
  width: 20px;
  height: 20px;
}

.chat-report-modal__panel {
  width: min(100%, 480px);
}

.chat-report-modal__title {
  margin: 0;
  padding: 20px 20px 14px;
  border-bottom: 1px solid #e4e4e6;
  font-size: 18px;
  text-align: center;
}

.chat-report-modal__body {
  display: grid;
  gap: 8px;
  padding: 18px 20px 22px;
}

.chat-report-modal__body label {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
}

.chat-report-modal__body select,
.chat-report-modal__body textarea {
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #cfd1d5;
  border-radius: 8px;
  color: #26262b;
  background: #fff;
  font: inherit;
}

.chat-report-modal__body select {
  min-height: 42px;
  padding: 0 10px;
}

.chat-report-modal__body textarea {
  min-height: 180px;
  padding: 10px;
  resize: vertical;
  line-height: 1.5;
}

.chat-report-modal__body select:focus,
.chat-report-modal__body textarea:focus {
  border-color: #4fc77b;
  outline: 0;
  box-shadow: 0 0 0 2px rgba(45, 187, 105, 0.14);
}

.chat-display-name-modal__panel {
  width: min(100%, 420px);
}

.chat-display-name-modal__body {
  display: grid;
  gap: 8px;
  padding: 22px 20px 26px;
}

.chat-display-name-modal__label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  font-weight: 600;
}

.chat-display-name-modal__label small {
  color: #8a8d92;
  font-size: 12px;
  font-weight: 400;
}

.chat-display-name-modal__body input {
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #cfd1d5;
  border-radius: 8px;
  color: #26262b;
  background: #fff;
  font: inherit;
  font-size: 16px;
}

.chat-display-name-modal__body input:focus {
  border-color: #4fc77b;
  outline: 0;
  box-shadow: 0 0 0 2px rgba(45, 187, 105, 0.14);
}

.chat-report-modal__confirmation {
  display: grid;
  gap: 6px;
  max-height: 58dvh;
  margin: 0;
  overflow-y: auto;
  padding: 18px 20px 22px;
}

.chat-report-modal__confirmation dt {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
}

.chat-report-modal__confirmation dd {
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}


.chat-detail__ribbon {
  z-index: 2;
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) 88px;
  align-items: center;
  border-bottom: 1px solid #e2e2e5;
  background: #fff;
  box-shadow: 0 2px 8px rgba(35, 45, 55, 0.08);
}

.chat-detail__theme {
  overflow: hidden;
  margin: 0;
  padding: 7px 14px;
  border-bottom: 1px solid #e2e2e5;
  background: #fff;
  color: #34343a;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-detail__theme span {
  font-weight: 400;
}

.chat-detail__theme strong {
  font-weight: 700;
}

.chat-detail__ribbon-actions,
.chat-detail__composer-actions {
  display: flex;
  align-items: center;
  min-width: 0;
}

.chat-detail__ribbon-actions--left,
.chat-detail__composer-actions--left {
  justify-content: flex-start;
}

.chat-detail__ribbon-actions--right,
.chat-detail__composer-actions--right {
  justify-content: flex-end;
}

.chat-detail__back,
.chat-detail__menu,
.chat-detail__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 0;
  color: #34343a;
  background: transparent;
}

.chat-detail__back {
  gap: 4px;
  padding: 0 8px;
  font-size: 14px;
  white-space: nowrap;
}

.chat-detail__menu {
  font-size: 20px;
}

.chat-detail__partner {
  display: inline-flex;
  align-items: center;
  justify-self: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  color: inherit;
  text-decoration: none;
}

.chat-detail__partner--link {
  cursor: pointer;
}

.chat-detail__header-avatar {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
}

.chat-detail__nickname {
  overflow: hidden;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-detail__messages {
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 14px 12px 22px;
  overscroll-behavior-y: contain;
  scrollbar-gutter: stable;
}

.chat-detail__history-loading {
  position: sticky;
  z-index: 5;
  top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: max-content;
  height: 36px;
  margin: 0 auto -36px;
  padding: 6px 12px;
  border: 1px solid #e4e4e7;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
  color: #525263;
  font-size: 13px;
  pointer-events: none;
}

.chat-detail__history-loading .ec-loading__spinnerVisual {
  width: 20px;
  height: 20px;
}

.chat-detail__date-separator {
  display: flex;
  justify-content: center;
  margin: 10px 0 14px;
}

.chat-detail__date-separator span {
  padding: 4px 11px;
  border-radius: 12px;
  color: #fff;
  background: rgba(55, 68, 79, 0.42);
  font-size: 11px;
  line-height: 1.4;
}

.chat-detail__message {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin-top: 7px;
}

.chat-detail__message--own {
  justify-content: flex-end;
}

.chat-detail__avatar-slot {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
}

.chat-detail__avatar {
  display: block;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
}

.chat-detail__bubble {
  position: relative;
  max-width: 100%;
  margin: 0;
  padding: 9px 12px;
  border-radius: 16px;
  color: #19191c;
  background: #fff;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.chat-detail__message-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 75%;
  min-width: 0;
}

.chat-detail__message-content--own {
  align-items: flex-end;
}

.chat-detail__reaction-target {
  border: 0;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.chat-detail__reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.chat-detail__reaction {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  min-height: 30px;
  padding: 2px 7px;
  border: 1px solid rgba(255, 255, 255, 0.76);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  font-size: 17px;
  line-height: 1;
}

.chat-detail__reaction:not(.chat-detail__reaction--readonly) {
  cursor: pointer;
}

.chat-reaction-picker {
  position: fixed;
  z-index: 1060;
  inset: 0;
}

.chat-reaction-picker__panel {
  position: fixed;
  box-sizing: border-box;
  width: 312px;
  padding: 12px;
  border: 1px solid #dedee3;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(20, 27, 34, 0.22);
}

.chat-reaction-picker__presets {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
}

.chat-reaction-picker__presets button,
.chat-reaction-picker__custom button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 23px;
}

.chat-reaction-picker__presets button:hover,
.chat-reaction-picker__presets button:focus-visible,
.chat-reaction-picker__custom button:hover,
.chat-reaction-picker__custom button:focus-visible {
  background: #f1f3f5;
}

.chat-reaction-picker__custom {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px;
  gap: 6px;
  margin-top: 10px;
}

.chat-reaction-picker__custom input {
  min-width: 0;
  min-height: 44px;
  padding: 8px 11px;
  border: 1px solid #c7c9ce;
  border-radius: 10px;
  font-size: 16px;
}

.chat-reaction-picker__custom .chat-reaction-picker__input--error {
  border-color: #d7373f;
  box-shadow: 0 0 0 1px #d7373f;
}


.chat-detail__message--partner .chat-detail__bubble::before {
  position: absolute;
  bottom: 4px;
  left: -5px;
  width: 12px;
  height: 12px;
  background: #fff;
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
  content: '';
}

.chat-detail__message--own .chat-detail__bubble {
  background: #8de55d;
}

.chat-detail__message--own .chat-detail__bubble::after {
  position: absolute;
  right: -5px;
  bottom: 4px;
  width: 12px;
  height: 12px;
  background: #8de55d;
  clip-path: polygon(0 0, 100% 100%, 0 100%);
  content: '';
}

.chat-detail__meta {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  padding-bottom: 2px;
  color: rgba(255, 255, 255, 0.96);
  font-size: 10px;
  line-height: 1.35;
  text-shadow: 0 1px 2px rgba(28, 38, 46, 0.38);
  white-space: nowrap;
}

.chat-detail__meta--own {
  align-items: flex-end;
}

.chat-detail__meta--partner {
  align-items: flex-start;
}

.chat-detail__composer {
  z-index: 2;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  align-items: end;
  gap: 6px;
  padding: 8px max(8px, env(safe-area-inset-right)) calc(8px + env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left));
  border-top: 1px solid #e3e3e6;
  background: #fff;
}

.chat-detail__composer-actions {
  min-height: 36px;
}

.chat-detail__file-input {
  display: none;
}

.chat-detail__icon-button {
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  color: #56565d;
  font-size: 20px;
}

.chat-detail__send {
  color: #21aa5b;
}

.chat-detail__input {
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: 36px;
  max-height: 116px;
  resize: none;
  padding: 7px 12px;
  border: 1px solid #d7d7dc;
  border-radius: 18px;
  outline: 0;
  color: #26262b;
  background: #f7f7f8;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
}

.chat-detail__input:focus {
  border-color: #4fc77b;
  box-shadow: 0 0 0 2px rgba(45, 187, 105, 0.14);
}

.chat-detail__input--error,
.chat-detail__input--error:focus {
  border-color: #d43a3a;
  box-shadow: 0 0 0 1px #d43a3a;
}

.chat-detail__back:focus-visible,
.chat-detail__menu:focus-visible,
.chat-detail__icon-button:focus-visible {
  outline: 2px solid #2dbb69;
  outline-offset: -2px;
}

.chat-detail__back:active,
.chat-detail__menu:active,
.chat-detail__icon-button:active {
  background: #eef7f1;
}

.chat-image-confirm__panel {
  width: min(100%, 420px);
}

.chat-image-confirm__preview {
  display: block;
  width: 100%;
  max-height: 52vh;
  background: #f3f3f5;
  object-fit: contain;
}

.chat-image-confirm__message {
  padding-bottom: 8px;
}

.chat-image-confirm__notice {
  margin: 0;
  padding: 0 20px 20px;
  color: #77777d;
  font-size: 12px;
  text-align: center;
}

.chat-image-viewer {
  position: fixed;
  z-index: 9100;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  background: rgba(0, 0, 0, 0.86);
}

.chat-image-viewer__dialog {
  display: flex;
  max-width: 100%;
  max-height: 100%;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.chat-image-viewer__controls {
  display: flex;
  flex: none;
  justify-content: flex-end;
}

.chat-image-viewer__panel {
  position: relative;
  display: flex;
  max-width: 280px;
  max-height: 280px;
  align-items: center;
  justify-content: center;
}

.chat-image-viewer__panel img {
  display: block;
  width: auto;
  height: auto;
  max-width: 280px;
  max-height: 280px;
  object-fit: contain;
}

.chat-image-viewer__close {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  font-size: 22px;
}

.chat-detail__bubble--image {
  box-sizing: border-box;
  width: auto;
  height: auto;
  max-width: none;
  padding: 10px 12px;
}

.chat-detail__image-button {
  display: inline-flex;
  overflow: hidden;
  border: 0;
  font: inherit;
  cursor: pointer;
}

.chat-detail__image-button img {
  display: block;
  width: auto;
  height: auto;
  max-width: 120px;
  max-height: 120px;
  object-fit: contain;
}

.chat-detail__image-deleted {
  display: inline-block;
  padding: 10px 12px;
  color: #77777d;
  font-size: 12px;
  white-space: normal;
}

.chat-detail__image-button:focus-visible,
.chat-image-viewer__close:focus-visible {
  outline: 2px solid #2dbb69;
  outline-offset: 2px;
}

.chat-action-menu {
  position: fixed;
  z-index: 8800;
  inset: 0;
  background: rgba(19, 25, 31, 0.28);
}

.chat-action-menu__panel {
  position: absolute;
  top: 54px;
  right: max(16px, calc((100vw - 940px) / 2));
  display: grid;
  width: 260px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 36px rgba(31, 39, 46, 0.22);
}

.chat-action-menu__panel button {
  min-height: 52px;
  padding: 0 20px;
  border: 0;
  border-bottom: 1px solid #e8e8eb;
  color: #24262a;
  background: transparent;
  text-align: left;
}

.chat-action-menu__panel button:last-child { border-bottom: 0; }
.chat-action-menu__panel button:hover,
.chat-action-menu__panel button:focus-visible { background: #f3f4f6; }
.chat-action-menu__link {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 0 20px;
  border-bottom: 1px solid #e8e8eb;
  color: #24262a;
  background: transparent;
  text-align: left;
  text-decoration: none;
}
.chat-action-menu__link:last-child { border-bottom: 0; }
.chat-action-menu__link:hover,
.chat-action-menu__link:focus-visible { background: #f3f4f6; }
.chat-action-menu__panel button:disabled { color: #92959a; background: #f6f6f7; }
.chat-menu-toggle { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.chat-menu-toggle__switch { position: relative; flex: 0 0 42px; width: 42px; height: 24px; border-radius: 999px; background: #c7c9cd; transition: background-color .15s; }
.chat-menu-toggle__switch::after { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.25); content: ""; transition: transform .15s; }
.chat-menu-toggle__switch--on { background: #2dbb69; }
.chat-menu-toggle__switch--on::after { transform: translateX(18px); }

@media (max-width: 767px) {
  .chat-reaction-picker {
    display: flex;
    align-items: flex-end;
    padding: 10px;
    background: rgba(0, 0, 0, 0.28);
  }

  .chat-reaction-picker__panel {
    position: static;
    width: 100%;
    border: 0;
    border-radius: 16px;
  }

  .chat-action-menu { display: flex; align-items: flex-end; padding: 10px; }
  .chat-action-menu__panel {
    position: static;
    width: 100%;
    border: 0;
    border-radius: 16px;
    box-shadow: 0 10px 36px rgba(0, 0, 0, 0.24);
  }
  .chat-action-menu__panel button { min-height: 54px; text-align: center; }
  .chat-action-menu__link { min-height: 54px; justify-content: center; text-align: center; }
}

@media (min-width: 768px) {
  .chat-detail {
    height: calc(100vh - 54px);
    height: calc(100dvh - 54px);
  }

  .chat-detail__messages {
    padding-right: 16px;
    padding-left: 16px;
  }

  .chat-image-viewer__panel,
  .chat-image-viewer__panel img {
    max-width: 600px;
    max-height: 600px;
  }

  .chat-detail__image-button img {
    max-width: 180px;
    max-height: 180px;
  }
}
</style>
